import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ButtonVariant, ConditionalWrapper, IconColor, IconName, IconSize, Popover as CotopaxiPopover, PopoverSize, usePopoverHandlers, } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { ABTestCookie, Cookie } from '@as-react/common-types';
import { EventEnum } from '@as-react/common-types';
import { CookieUtil, EventUtil } from '@as-react/common-utils';
import { useIsClient, useMediaQueryMatchesOnDesktop } from '@yonderland/andes-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { BasketReminderNotification } from '../../../components/basket/basket-reminder-notification/BasketReminderNotification';
import { LockedBasketNotificationMessage } from '../../../components/notification-message/LockedBasketNotificationMessage';
import { ADOBE_TARGET_PRESOLDEN } from '../../../constants/AdobeTargetConstants';
import { ADD_VOUCHER_PARAM_KEY } from '../../../constants/BasketConstants';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { HAS_ACTIVE_SESSION, IS_NEW_SESSION } from '../../../constants/StorageKeysConstants';
import { BasketPopoverContainer } from '../../../containers/BasketPopoverContainer';
import { useABTest } from '../../../hooks/useABTest';
import { useFeatureToggle } from '../../../hooks/useFeatureToggle';
import { YonderlandMediatorGroup } from '../../../mediator/types/YonderlandMediatorTypes';
import YonderlandMediator from '../../../mediator/YonderlandMediator';
import { basketActions } from '../../../redux/basket/actions/BasketActions.creators';
import { makeGetBasketPromotionProductsState, makeGetBasketTotalQuantityState, } from '../../../redux/basket/selectors/BasketSelector';
import SessionStorageUtil from '../../../util/SessionStorageUtil';
import { getQuerystringByName } from '../../../util/URLParamUtil';
const BasketMenuItemContainer = ({ basketOverviewLink, freeDeliveryMessage, enableBasketReminderNotification, enableBasketReminderNotificationABTest, }) => {
    const { t } = useTranslation(I18N_NAMESPACES.navigation);
    const { anchorEl, handlePopoverOpen, handlePopoverClose } = usePopoverHandlers();
    const { pageInfo } = useAemConfig();
    const { isConfirmationPage, isPDPPage, isHomePage, isBrandPage, isBrandAsCategoryPage, isListerPage, isCategoryPage, isBasketOverviewPage, } = pageInfo;
    const isCommercialPage = isHomePage || isPDPPage || isBrandPage || isBrandAsCategoryPage || isListerPage || isCategoryPage;
    const isDesktop = useMediaQueryMatchesOnDesktop({ isSsr: true });
    const dispatch = useDispatch();
    const productAmount = useSelector(makeGetBasketTotalQuantityState());
    const activeBasketReminder = useFeatureToggle('active_basket_reminder');
    const noBasketPopup = useFeatureToggle('no_basket_popup_on_click');
    const buttonEl = useRef(null);
    const [isBasketChanged, setIsBasketChanged] = useState(false);
    const promotionProducts = useSelector(makeGetBasketPromotionProductsState());
    const [firstVisit, setFirstVisit] = useState(false);
    const { hasBCookie: showBasketReminderABTest } = useABTest(ABTestCookie.BASKET_REMINDER_NOTIFICATION);
    const isClient = useIsClient();
    const addVoucherParam = isClient ? getQuerystringByName(window.location.search, ADD_VOUCHER_PARAM_KEY) : false;
    useEffect(() => {
        EventUtil.listen(EventEnum.BASKET_CHANGED, () => {
            setIsBasketChanged(true);
            SessionStorageUtil.setItem(IS_NEW_SESSION, false);
        });
    }, []);
    useEffect(() => {
        var _a;
        if (activeBasketReminder &&
            isCommercialPage &&
            productAmount > 0 &&
            isDesktop &&
            !isBasketChanged &&
            SessionStorageUtil.getJsonItem(IS_NEW_SESSION) &&
            CookieUtil.get(Cookie.IS_BASKET_REMINDER_CALLED) !== 'true') {
            if (!showBasketReminderNotification()) {
                (_a = buttonEl.current) === null || _a === void 0 ? void 0 : _a.click();
            }
            CookieUtil.set(Cookie.IS_BASKET_REMINDER_CALLED, 'true');
        }
    }, [productAmount]);
    useEffect(() => {
        var _a;
        const hasPromotionProduct = promotionProducts.length > 0;
        if (!hasPromotionProduct) {
            return;
        }
        const promotion = (_a = promotionProducts.find(product => !!product.promotion)) === null || _a === void 0 ? void 0 : _a.promotion;
        const minimumBasketSize = promotion === null || promotion === void 0 ? void 0 : promotion.minimumBasketSize;
        !addVoucherParam &&
            YonderlandMediator.publish({
                event: ADOBE_TARGET_PRESOLDEN,
                group: YonderlandMediatorGroup.BASKET,
                data: {
                    origin: 'BasketMenuItemContainer',
                    hasPreSoldenProduct: hasPromotionProduct,
                    isPreSoldenFulfilled: Boolean(hasPromotionProduct && minimumBasketSize && productAmount >= minimumBasketSize),
                    hasMultipleProducts: productAmount > 1,
                },
            });
    }, [promotionProducts]);
    useEffect(() => {
        if (!isConfirmationPage) {
            dispatch(basketActions.createGetAction());
        }
        if (SessionStorageUtil.getJsonItem(IS_NEW_SESSION) === null) {
            SessionStorageUtil.setItem(IS_NEW_SESSION, true);
        }
        if (SessionStorageUtil.getJsonItem(HAS_ACTIVE_SESSION) === null) {
            setFirstVisit(true);
            SessionStorageUtil.setItem(HAS_ACTIVE_SESSION, true);
        }
    }, []);
    const withHandler = isDesktop && !noBasketPopup;
    const handleClick = (event) => {
        dispatch(basketActions.createGetAction());
        event && handlePopoverOpen(event);
    };
    const basketPopoverTitle = () => productAmount
        ? t(productAmount > 1 ? 'basket.popover.title_plural' : 'basket.popover.title', {
            count: productAmount,
        })
        : t('basket.popover.title.default');
    const isFirstVisitAndBasketFilled = () => !isBasketOverviewPage && firstVisit && productAmount > 0 && !isBasketChanged;
    const showBasketReminderNotification = () => isClient &&
        (!enableBasketReminderNotification || (enableBasketReminderNotificationABTest && !showBasketReminderABTest))
        ? false
        : isFirstVisitAndBasketFilled();
    return (_jsxs("div", { "data-analytics-bannerid": "header-shopping-cart", children: [_jsx(LockedBasketNotificationMessage, {}), _jsx(ConditionalWrapper, { condition: showBasketReminderNotification(), wrapper: children => (_jsx(BasketReminderNotification, { hrefViewCart: basketOverviewLink, children: children })), children: _jsx(Button, { badge: productAmount, badgeAnimated: isFirstVisitAndBasketFilled(), variant: ButtonVariant.NAVIGATION, href: withHandler ? undefined : basketOverviewLink, onClick: withHandler ? handleClick : undefined, dataQA: "basket", dataBadgeQA: "basket_counter", icon: {
                        name: IconName.CART,
                        size: IconSize.MAJOR,
                    }, text: "Shopping cart", innerRef: buttonEl }) }), _jsx(CotopaxiPopover, { noPadding: true, dataQA: "basket_popover", anchorEl: anchorEl, title: basketPopoverTitle(), onClose: handlePopoverClose, rightElement: _jsx(Button, { variant: ButtonVariant.TRANSPARENT, onClick: handlePopoverClose, icon: {
                        name: IconName.CLOSE,
                        color: IconColor.ACTION,
                        size: IconSize.MAJOR,
                    } }), size: PopoverSize.EXTRA_LARGE, children: _jsx(BasketPopoverContainer, { freeDeliveryMessage: freeDeliveryMessage, hrefViewCart: basketOverviewLink }) })] }));
};
export default BasketMenuItemContainer;
